import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, Minus, Plus, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { API } from "@/data/site";

const formatINR = (n) => `₹${n.toLocaleString("en-IN")}`;

const inputCls =
  "w-full border border-white/10 bg-stone-950/70 px-4 py-3 text-sm font-light text-stone-100 placeholder:text-stone-600 outline-none transition-colors duration-300 focus:border-amber-600/60";

const CartDrawer = () => {
  const { items, setQty, remove, clear, total, open, setOpen } = useCart();
  const [checkout, setCheckout] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [placedId, setPlacedId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = (v) => {
    setOpen(v);
    if (!v) {
      setTimeout(() => {
        setCheckout(false);
        setPlacedId(null);
      }, 300);
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
      });
      setPlacedId(res.data.order_id);
      clear();
      toast.success("Order request received by the tea house.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place your order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={close}>
      <SheetContent
        data-testid="cart-drawer"
        className="flex w-full flex-col border-l border-white/10 bg-stone-950 p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b border-white/5 px-7 py-6">
          <SheetTitle className="flex items-center gap-3 font-serif text-2xl tracking-tight text-stone-100">
            <ShoppingBag className="h-5 w-5 text-amber-500" />
            Your Caddy
          </SheetTitle>
        </SheetHeader>

        {placedId ? (
          <div data-testid="order-success" className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center">
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
            >
              <CheckCircle2 className="h-14 w-14 text-amber-500" />
            </motion.div>
            <div>
              <p className="font-serif text-3xl tracking-tight text-stone-100">Order received</p>
              <p className="mt-2 text-sm font-light text-stone-400">
                Reference <span className="text-amber-500">{placedId}</span>
              </p>
              <p className="mx-auto mt-4 max-w-xs text-sm font-light leading-relaxed text-stone-500">
                Our tea house will call you shortly to confirm. Payment is collected on delivery.
              </p>
            </div>
            <button
              data-testid="order-continue-button"
              onClick={() => close(false)}
              className="mt-2 rounded-full border border-white/15 px-7 py-3 text-sm font-light text-stone-300 transition-colors duration-300 hover:border-amber-600/60 hover:text-amber-500"
            >
              Continue browsing
            </button>
          </div>
        ) : items.length === 0 ? (
          <div data-testid="cart-empty" className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
            <ShoppingBag className="h-10 w-10 text-stone-700" />
            <p className="font-serif text-2xl tracking-tight text-stone-300">Your caddy is empty</p>
            <p className="max-w-xs text-sm font-light text-stone-500">
              Wander the collection and choose a leaf worth your evening.
            </p>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-5 overflow-y-auto px-7 py-6">
              <AnimatePresence initial={false}>
                {items.map((i) => (
                  <motion.div
                    key={i.slug}
                    data-testid={`cart-item-${i.slug}`}
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    className="flex gap-4 border border-white/5 bg-stone-900/40 p-4"
                  >
                    <div className="h-20 w-16 shrink-0 overflow-hidden">
                      <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="flex flex-1 flex-col justify-between">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-serif text-lg leading-tight tracking-tight text-stone-100">{i.name}</p>
                          <p className="mt-0.5 text-[11px] uppercase tracking-[0.2em] text-stone-500">
                            {i.unit} · {formatINR(i.price)}
                          </p>
                        </div>
                        <button
                          data-testid={`cart-remove-${i.slug}`}
                          onClick={() => remove(i.slug)}
                          className="text-stone-600 transition-colors duration-300 hover:text-amber-500"
                          aria-label={`Remove ${i.name}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-2 flex items-center gap-3">
                        <button
                          data-testid={`cart-qty-minus-${i.slug}`}
                          onClick={() => setQty(i.slug, i.qty - 1)}
                          className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 text-stone-300 transition-colors duration-300 hover:border-amber-600/60 hover:text-amber-500"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span data-testid={`cart-qty-${i.slug}`} className="w-5 text-center text-sm text-stone-200">
                          {i.qty}
                        </span>
                        <button
                          data-testid={`cart-qty-plus-${i.slug}`}
                          onClick={() => setQty(i.slug, i.qty + 1)}
                          className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 text-stone-300 transition-colors duration-300 hover:border-amber-600/60 hover:text-amber-500"
                          aria-label="Increase quantity"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <div className="border-t border-white/5 px-7 py-6">
              <div className="mb-5 flex items-center justify-between">
                <span className="text-sm font-light text-stone-400">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-serif text-2xl tracking-tight text-amber-500">
                  {formatINR(total)}
                </span>
              </div>

              {!checkout ? (
                <>
                  <button
                    data-testid="checkout-button"
                    onClick={() => setCheckout(true)}
                    className="w-full rounded-full bg-amber-600 px-7 py-4 text-sm font-medium text-stone-950 transition-all duration-300 hover:bg-amber-500 hover:shadow-[0_0_30px_rgba(217,119,6,0.3)]"
                  >
                    Request this order
                  </button>
                  <p className="mt-3 text-center text-[11px] font-light text-stone-600">
                    Pay on delivery — the tea house confirms every order by phone.
                  </p>
                </>
              ) : (
                <form data-testid="checkout-form" onSubmit={placeOrder} className="space-y-4">
                  <input
                    data-testid="checkout-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Full name"
                    className={inputCls}
                  />
                  <input
                    data-testid="checkout-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="Email"
                    className={inputCls}
                  />
                  <input
                    data-testid="checkout-phone-input"
                    required
                    minLength={8}
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Phone — we call to confirm"
                    className={inputCls}
                  />
                  <textarea
                    data-testid="checkout-address-input"
                    required
                    minLength={10}
                    rows={3}
                    value={form.address}
                    onChange={set("address")}
                    placeholder="Delivery address"
                    className={`${inputCls} resize-none`}
                  />
                  <button
                    data-testid="place-order-button"
                    type="submit"
                    disabled={placing}
                    className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-amber-600 px-7 py-4 text-sm font-medium text-stone-950 transition-all duration-300 hover:bg-amber-500 disabled:opacity-60"
                  >
                    {placing && <Loader2 className="h-4 w-4 animate-spin" />}
                    {placing ? "Placing..." : `Place order · ${formatINR(total)}`}
                  </button>
                  <p className="text-center text-[11px] font-light text-stone-600">
                    Pay on delivery — no online payment taken yet.
                  </p>
                </form>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
